import Joi from 'joi';
import emojiRegex from 'emoji-regex';

export const minPasswordLength = 8;
export const maxPasswordLength = 64;

// HACK remove the global flag from the resulting regex that comes from emojiRegex to keep Joi... joyous...
const removeFlagsRegex = /(.*\/).*$/;
const emojiRegexStr = emojiRegex().toString();
const emojiRegexStrNoFlags = removeFlagsRegex.exec(emojiRegexStr)![1];

const schemaConfig = {
    password: Joi.string()
        .min(minPasswordLength)
        .max(maxPasswordLength)
        .pattern(new RegExp("[a-zA-Z]+"), { name: 'at least one letter' })
        .pattern(new RegExp("[0-9]+"), { name: 'at least one number' })
        .pattern(new RegExp("[ !\"#$%&'()*+,-./:;<=>?@[\\]^`{|}~]+"), { name: 'at least one non-alpha, non-numeric' })
        .pattern(new RegExp(emojiRegexStrNoFlags), { name: 'emojis', invert: true })
        .required()
};

const schema = Joi.object(schemaConfig).with('password', 'password');

export enum PasswordError {
    // Password did not meet minimum length requirement
    TooShort = 'Password is too short.',

    // Password exceeded maximum length requirement
    TooLong = 'Password is too long.',

    // Complexity was not met (e.g. at least one letter, number and other)
    NotComplex = 'Password must include at least one letter, number and special character.',

    // Has invalid characters / input
    HasInvalidChars = 'Password has invalid charsacters.',

    // some other error
    Unknown = 'Unknown error.',
}

class ValidationService {

    /**
     * Validate a password.
     * left as optional so as to subject any combination to our validation, instead of a split between TypeScript and ours.
     * @param password 
     * 
     * @returns A validation error if one was found, undefined otherwise
     */
    async getPasswordValidationError(password: string) {
        // The current implementation for this method has been made async on purpose, to account for possible
        // future enhancements where we might need to invoke a remote call for extended validation purposes
        // e.g. checking the passwords don't contain dictionary words

        try {
            await schema.validateAsync({ password });
        } catch (err) {
            // If error came from Joi, return the error to the caller
            // Else rethrow
            if ((err as Joi.ValidationError).isJoi) {
                return mapJoiErrorToPasswordError(err as Joi.ValidationError);
            }
            return PasswordError.Unknown;
        }

        // Map the Joi errors to our own PasswordError
        // Joi errors will be asserted on based on the `type` property of the error
        function mapJoiErrorToPasswordError(err: Joi.ValidationError) {

            // Going to just observe the first item
            // Might need to observe the other items at some point
            const validationErrorItem = err.details[0];

            let passwordError: PasswordError = PasswordError.Unknown;

            // The `type` property might not be the best way to assert the mapping
            // For further reference regarding the Joi types, see https://github.com/hapijs/joi/tree/master/lib/types
            switch (validationErrorItem.type) {
                case 'string.min':
                    passwordError = PasswordError.TooShort;
                    break;
                case 'string.max':
                    passwordError = PasswordError.TooLong;
                    break;
                case 'string.pattern.name':
                    passwordError = PasswordError.NotComplex;
                    break;
                case 'string.pattern.invert.name':
                    passwordError = PasswordError.HasInvalidChars;
                    break;
                default:
                    passwordError = PasswordError.Unknown;
                    break;
            }
            return passwordError;
        }
    }
}

export default (new ValidationService());